// SIGNUP-INTENT (symbols.app/built-with) — the sign-up link's preset, plan and
// source, carried across whichever sign-in follows.
//
// The email path already forwards them on the register body
// (pages/auth/signup.js). A Google / GitHub sign-up never touches that body,
// and a verify-email-then-sign-in loses the query. So the signup page STASHES
// the intent here, and the first explicit sign-in afterwards
// (functions/fetch.js initializeUserSession) SENDS it through the existing
// sdk.updateMe as `signupIntent`. The server stores it first-touch only, for
// new accounts only, and installs the preset (server
// auth/services/SignupIntentService.js). A paid plan also queues ONE
// post-sign-in route, /admin/usage?checkout=<plan>, which that page turns into
// the real checkout (pages/admin/usage.js). A `?claim=credits` link rides
// along as `claimCredits`, so a Google / GitHub sign-up claims the sign-up
// credits like the email form does.
//
// D9 — EVERY SIGN-UP CLAIMS (2026-10-25). With nothing stashed (a plain
// Google / GitHub sign-up, no link), the first sign-in on this browser sends
// `{ claimCredits: true }` alone. The server decides whether the account is
// new (created in the last 24 h, else `not_a_new_account`) and claims once
// per person (a repeat is `alreadyClaimed`) — the client never guesses. After
// a definite answer a per-user flag (`signup_claim_answered:<userId>`) stops
// later sign-ins from this browser sending it again. The SDK's updateMe
// resolves with no data for an intent-only PATCH, and the server answers 2xx
// only with an outcome, so a RESOLVED request is the definite answer; a
// rejected one (offline, 5xx) leaves no flag, and the next sign-in asks again.
//
// FA204/FA207 / frank trap: every export is self-contained (keys, limits and
// helpers inlined), as in postOAuthNext.js — module-scope names do not survive
// a per-export frank.toJSON.

export const stashSignupIntent = () => {
  const KEY = 'signup_intent'
  if (typeof window === 'undefined') return false
  try {
    const params = new URL(window.location.href).searchParams
    const fields = {
      source: 'utm_source',
      medium: 'utm_medium',
      campaign: 'utm_campaign',
      ref: 'ref',
      plan: 'plan'
    }
    const acquisition = {}
    Object.keys(fields).forEach((k) => {
      const v = params.get(fields[k])
      if (v) acquisition[k] = String(v).slice(0, 200)
    })
    const preset = String(params.get('preset') || '').slice(0, 64)
    // `?claim=credits` — the sign-up credits a Google / GitHub sign-up would
    // otherwise lose (the email form claims on the register body).
    const claimCredits = params.get('claim') === 'credits'
    if (!preset && !Object.keys(acquisition).length && !claimCredits) return false
    const stash = { preset: preset || null, acquisition, at: Date.now() }
    if (claimCredits) stash.claimCredits = true
    window.localStorage.setItem(KEY, JSON.stringify(stash))
    return true
  } catch (_) {
    return false
  }
}

export const sendSignupIntent = function sendSignupIntent() {
  const KEY = 'signup_intent'
  const CHECKOUT_KEY = 'signup_checkout_next'
  const ANSWERED_PREFIX = 'signup_claim_answered:'
  const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
  if (typeof window === 'undefined') return null
  const root = (this && this.state && this.state.root) || null
  const userId = String((root && (root.userId || (root.currentUser && root.currentUser.id))) || '')
  const answeredKey = userId ? ANSWERED_PREFIX + userId : ''
  // Never blocks or fails the sign-in: the server answers when it answers.
  // A resolved answer marks this person on this browser (see the header).
  const send = (signupIntent) => {
    try {
      const sdk = this && this.sdk
      const pending =
        sdk && typeof sdk.updateMe === 'function' ? sdk.updateMe({ signupIntent }) : null
      if (pending && typeof pending.then === 'function') {
        pending.then(
          () => {
            if (!answeredKey) return
            try {
              window.localStorage.setItem(answeredKey, String(Date.now()))
            } catch (_) {}
          },
          () => {}
        )
      }
    } catch (_) {}
  }
  let intent = null
  try {
    intent = JSON.parse(window.localStorage.getItem(KEY) || 'null')
    // consumed once, whatever happens next
    window.localStorage.removeItem(KEY)
  } catch (_) {
    return null
  }
  const fresh =
    !!intent &&
    typeof intent === 'object' &&
    !!intent.at &&
    Date.now() - Number(intent.at) <= MAX_AGE_MS
  if (!fresh) {
    // Nothing stashed: the claim alone, once per person on this browser, and
    // only for a known person (the flag needs the id).
    if (!answeredKey) return null
    try {
      if (window.localStorage.getItem(answeredKey)) return null
    } catch (_) {
      return null
    }
    const claimOnly = { claimCredits: true }
    send(claimOnly)
    return claimOnly
  }
  const acquisition =
    intent.acquisition && typeof intent.acquisition === 'object' ? intent.acquisition : {}
  const signupIntent = { acquisition }
  if (intent.preset) signupIntent.preset = intent.preset
  // The server claims once per user, new accounts only (server
  // auth/services/SignupIntentService.js) — a repeat is `alreadyClaimed`.
  if (intent.claimCredits === true) signupIntent.claimCredits = true
  send(signupIntent)
  const plan = String(acquisition.plan || '').toLowerCase()
  if (plan === 'pro' || plan === 'scale') {
    try {
      window.localStorage.setItem(
        CHECKOUT_KEY,
        JSON.stringify({ path: '/admin/usage?checkout=' + plan, at: Date.now() })
      )
    } catch (_) {}
  }
  return signupIntent
}

// The queued post-sign-in route, handed out ONCE, only in the one shape this
// file writes, and only within an hour of the sign-in that queued it.
export const takeSignupCheckoutNext = () => {
  const CHECKOUT_KEY = 'signup_checkout_next'
  const MAX_AGE_MS = 60 * 60 * 1000
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(CHECKOUT_KEY)
    if (!raw) return null
    window.localStorage.removeItem(CHECKOUT_KEY)
    const v = JSON.parse(raw)
    if (!v || typeof v.path !== 'string') return null
    if (!/^\/admin\/usage\?checkout=(pro|scale)$/.test(v.path)) return null
    if (!v.at || Date.now() - Number(v.at) > MAX_AGE_MS) return null
    return v.path
  } catch (_) {
    return null
  }
}
